<template>
  <v-container fluid class="home-view fill-height pa-0">
    <v-row no-gutters class="fill-height">
      <v-col cols="4" class="fill-height white border-r d-flex flex-column">
        <div class="pa-3 flex-grow-1 overflow-y-auto">
          <div class="d-flex align-center justify-space-between mb-3">
            <h2 class="text-h6 font-weight-regular mb-0">首页统计</h2>
            <div class="text-caption text-right">
              <v-icon x-small color="primary">mdi-map-marker</v-icon>
              当前区域: {{ currentRegionDisplay }}
            </div>
          </div>

          <v-alert dense type="info" border="left" class="mb-3">
            地图展示：本村行政区边界、遥感影像、已投保地块（黄色）、未投保地块（绿色）
          </v-alert>

          <v-card outlined class="pa-3 mb-3">
            <div class="text-subtitle-2 mb-2">投保情况</div>
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption grey--text">投保总面积(亩)</div>
                <div class="text-subtitle-1">{{ formatNumber(stats.insured_area_total) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">保单个数</div>
                <div class="text-subtitle-1">{{ formatInt(stats.policy_count_total) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">投保户数</div>
                <div class="text-subtitle-1">{{ formatInt(stats.insured_household_total) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">大户/散户</div>
                <div class="text-subtitle-1">{{ formatInt(stats.large_household_count) }} / {{
                  formatInt(stats.retail_household_count) }}</div>
              </v-col>
            </v-row>
          </v-card>

          <v-card outlined class="pa-3 mb-3">
            <div class="text-subtitle-2 mb-2">各作物面积（亩）</div>
            <v-list dense class="py-0">
              <v-list-item v-for="item in sortedCropAreas" :key="item.crop_type" class="px-1" style="min-height: 30px;">
                <v-list-item-content class="py-0">
                  <v-list-item-title class="text-body-2">{{ item.crop_type }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <span class="text-body-2">{{ formatNumber(item.insured_area) }}</span>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="sortedCropAreas.length > 0" class="my-1"></v-divider>
              <v-list-item v-if="sortedCropAreas.length > 0" class="px-1" style="min-height: 30px;">
                <v-list-item-content class="py-0">
                  <v-list-item-title class="text-body-2 font-weight-bold">总计</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <span class="text-body-2 font-weight-bold">{{ formatNumber(cropAreaTotal) }}</span>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="sortedCropAreas.length === 0" class="px-1" style="min-height: 30px;">
                <v-list-item-content class="py-0">
                  <v-list-item-title class="text-caption grey--text">暂无数据</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>

          <v-card outlined class="pa-3">
            <div class="text-subtitle-2 mb-2">地块情况（GPKG属性）</div>
            <v-row dense>
              <v-col cols="12">
                <div class="text-caption grey--text">地块总面积(亩)</div>
                <div class="text-subtitle-1">{{ formatNumber(stats.plot_area_total) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">水地面积(亩)</div>
                <div class="text-subtitle-1">{{ formatNumber(stats.water_plot_area) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">旱地面积(亩)</div>
                <div class="text-subtitle-1">{{ formatNumber(stats.dry_plot_area) }}</div>
              </v-col>
            </v-row>
          </v-card>
        </div>
      </v-col>

      <v-col cols="8" class="fill-height grey lighten-5 d-flex">
        <div id="home-ol-container" ref="mapContainer" class="fill-height map-container">
          <wgu-map target="home-ol-container" />
          <wgu-maploading-status />
          <wgu-app-logo />
          <wgu-bglayerswitcher :force-show="true" />
          <wgu-overviewmap v-if="overviewMapConfig" v-bind="overviewMapConfig" />
          <wgu-hover-tooltip />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Vue from 'vue'
import axios from '@/axios'
import { mapState } from 'vuex'
import OlMap from '@/components/ol/Map'
import HoverTooltip from '@/components/ol/HoverTooltip'
import AppLogo from '@/components/AppLogo'
import BgLayerSwitcher from '@/components/bglayerswitcher/BgLayerSwitcher.vue'
import OverviewMap from '@/components/overviewmap/OverviewMap.vue'
import MapLoadingStatus from '@/components/progress/MapLoadingStatus'
import AutoLocateArea from '@/mixins/AutoLocateArea'
import UnifiedMapLifecycle from '@/mixins/UnifiedMapLifecycle'
import { PLOT_WMS_URL, PLOT_WMS_LAYER, PLOT_WMS_STYLES } from '@/const/const.js'

export default {
  name: 'HomeView',
  mixins: [AutoLocateArea, UnifiedMapLifecycle],
  components: {
    'wgu-map': OlMap,
    'wgu-hover-tooltip': HoverTooltip,
    'wgu-app-logo': AppLogo,
    'wgu-bglayerswitcher': BgLayerSwitcher,
    'wgu-overviewmap': OverviewMap,
    'wgu-maploading-status': MapLoadingStatus
  },
  data () {
    const mapLayersConfig = [
      {
        type: 'IMAGEWMS',
        lid: 'wms-plot-layer',
        name: '地块信息 (WMS)',
        url: PLOT_WMS_URL,
        layers: PLOT_WMS_LAYER,
        format: 'image/png',
        transparent: true,
        projection: 'EPSG:4326',
        visible: true,
        isBaseLayer: false,
        crossOrigin: 'anonymous',
        zIndex: 5
      },
      {
        type: 'IMAGEWMS',
        lid: 'region-layer',
        name: '行政区划',
        ratio: 1.5,
        format: 'image/png',
        layers: 'ks:geo_district_code',
        url: 'https://geo.kungsong.com/geoserver/ks/wms',
        transparent: true,
        projection: 'EPSG:4326',
        isBaseLayer: false,
        visible: true,
        crossOrigin: 'anonymous',
        zIndex: 10
      },
      {
        type: 'XYZ',
        url: 'https://t4.tianditu.gov.cn/DataServer?T=cia_w&tk=2d986e569889fd35b5587da2ce9574b7&x={x}&y={y}&l={z}',
        lid: 'satelliteMark',
        name: '天地图标注',
        isBaseLayer: false,
        visible: true,
        crossOrigin: 'anonymous'
      },
      {
        type: 'XYZ',
        url: 'https://t6.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=2d986e569889fd35b5587da2ce9574b7',
        lid: 'satelliteBackground',
        name: '天地图卫星',
        isBaseLayer: true,
        visible: true,
        crossOrigin: 'anonymous'
      },
      {
        type: 'OSM',
        lid: 'osm-bg',
        name: 'OpenStreetMap',
        isBaseLayer: true,
        visible: false,
        crossOrigin: 'anonymous'
      }
    ]

    return {
      mapLayersConfig,
      overviewMapConfig: this.getOverviewMapConfig(),
      stats: {
        region_code: null,
        region_name: null,
        insured_area_total: 0,
        policy_count_total: 0,
        large_policy_count: 0,
        retail_policy_count: 0,
        insured_household_total: 0,
        large_household_count: 0,
        retail_household_count: 0,
        crop_areas: [],
        plot_area_total: 0,
        water_plot_area: 0,
        dry_plot_area: 0
      }
    }
  },
  computed: {
    ...mapState(['authorizedArea', 'user', 'selectedArea']),
    currentRegionDisplay () {
      return this.stats.region_name || this.selectedArea?.display_name || this.selectedArea?.region_name || this.user?.bound_region_name || '未绑定区域'
    },
    sortedCropAreas () {
      const cropAreas = Array.isArray(this.stats.crop_areas) ? [...this.stats.crop_areas] : []
      const priority = ['玉米', '水稻']

      const getPriorityIndex = (name) => {
        const cropName = String(name || '').trim()
        const index = priority.indexOf(cropName)
        return index === -1 ? Number.MAX_SAFE_INTEGER : index
      }

      return cropAreas.sort((left, right) => {
        const leftIndex = getPriorityIndex(left.crop_type)
        const rightIndex = getPriorityIndex(right.crop_type)

        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex
        }

        return String(left.crop_type || '').localeCompare(String(right.crop_type || ''), 'zh-Hans-CN')
      })
    },
    cropAreaTotal () {
      return this.sortedCropAreas.reduce((sum, item) => sum + Number(item.insured_area || 0), 0)
    }
  },
  watch: {
    authorizedArea: {
      handler () {
        this.updatePlotLayersUrl()
        this.fetchHomeStats()
      },
      deep: true
    },
    selectedArea: {
      handler () {
        this.updatePlotLayersUrl()
        this.fetchHomeStats()
      },
      deep: true
    }
  },
  async mounted () {
    Vue.prototype.$appConfig.mapLayers = this.mapLayersConfig

    try {
      await this.$store.dispatch('fetchAuthRegions')
    } catch (error) {
      console.error('Failed to load authorized area:', error)
    }

    this.startUnifiedMapLifecycle({
      containerRef: 'mapContainer',
      onMapReady: (map) => {
        if (!map.getTarget()) {
          const container = document.getElementById('home-ol-container')
          if (container) {
            map.setTarget(container)
          }
        }
        this.updatePlotLayersUrl()
        this.autoLocateToSelectedArea()
      },
      extraDelays: [200, 800, 1200]
    })

    this.fetchHomeStats()
  },
  methods: {
    formatNumber (value) {
      const num = Number(value || 0)
      return num.toFixed(2)
    },
    formatInt (value) {
      return Number(value || 0)
    },
    getOverviewMapConfig () {
      const appConfig = Vue.prototype.$appConfig || {}
      const overviewMapConf = appConfig.overviewMap
      if (overviewMapConf) {
        return { ...overviewMapConf, visible: false }
      }
      return { visible: false }
    },
    getVillageCodesForFilter () {
      const codes = []

      if (this.authorizedArea && this.authorizedArea.length > 0) {
        this.authorizedArea.forEach(auth => {
          const code = auth.place_id || auth.code
          if (code) codes.push(code)
        })
      }

      if (codes.length === 0) {
        const selectedCode = this.selectedArea?.place_id || this.selectedArea?.code
        if (selectedCode) codes.push(selectedCode)
      }

      if (codes.length === 0) {
        const boundCode = this.user?.bound_region_code
        if (boundCode) codes.push(boundCode)
      }

      if (codes.length === 0 && this.user?.auth_region && typeof this.user.auth_region === 'object') {
        const villages = this.user.auth_region.village || []
        villages.forEach(code => {
          if (code) codes.push(code)
        })
      }

      return [...new Set(codes)]
    },
    async fetchHomeStats () {
      try {
        const villageCodes = this.getVillageCodesForFilter()
        const params = {}
        if (villageCodes.length > 0) {
          params.village_code = villageCodes.join(',')
        }
        const response = await axios.get('/stats/home', { params })
        this.stats = {
          ...this.stats,
          ...response.data,
          crop_areas: Array.isArray(response.data?.crop_areas) ? response.data.crop_areas : []
        }
      } catch (error) {
        console.error('Failed to fetch home stats:', error)
      }
    },
    updatePlotLayersUrl () {
      if (!this.$map) return

      const villageCodes = this.getVillageCodesForFilter()
      if (villageCodes.length === 0) return

      const layers = this.$map.getLayers().getArray()
      const wmsLayer = layers.find(l => l.get('lid') === 'wms-plot-layer')
      if (!wmsLayer) return

      const source = wmsLayer.getSource()
      if (!source || typeof source.updateParams !== 'function') return

      const filter = `village_code IN ('${villageCodes.join("','")}') AND (hidden IS NULL OR hidden <> 1)`
      source.updateParams({
        CQL_FILTER: filter,
        STYLES: PLOT_WMS_STYLES.HOME
      })
      source.refresh()
    }
  }
}
</script>

<style>
.home-view {
  height: 100% !important;
}

.home-view .border-r {
  border-right: 1px solid #e0e0e0;
}

#home-ol-container {
  width: 100% !important;
  height: 100% !important;
}

.map-container {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}
</style>
